import React, {useEffect, useState} from "react";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "./../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/StepLabel";
import StepIcon from "./../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/StepIcon";
import StepButton from "@material-ui/core/StepButton";
import StepConnector from "./../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/StepConnector";
import { State } from "../../../../reducers/reducer";
import { useSelector } from "react-redux";
import clsx from "clsx";
import Typography from "@material-ui/core/Typography";
import {hasOffRoadData, getStepStatusOffroad} from "../../../../api/sustainability/allocation/offRoadUse/data";
import LoadingView from "../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/LoadingView";
import startAllocation from "../../../../api/sustainability/allocationSteps/startAllocation";
import OffRoadMainCardTables from "../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/OffRoadMainCardTables";
import { useAllocationContainerStyles } from "../../../../common/styles/allocationStyles";

interface Biotemplate {
    id: number;
    biofuel_type: string;
    raw_material_origin: string;
    fuel_type: string;
    raw_material: string;
    double_counted_norway: string;
    reduction_in_climate_emission: number;
    storage_terminal: string;
    available_quantity: number;
    allocated_quantity: number;
    allocated_eq_quantity: number;
}

const OffRoadContainer: React.FC = () => {
    const classes = useAllocationContainerStyles();

    const isSimulation: any = useSelector((state: State) => state.sustainability.main.isSimulation);
    const periodName: any = useSelector((state: State) => state.sustainability.main.activePeriod);
    const [availableVol, setAvailableVol] = useState({});
    const [generalBiotemplates, setGeneralBiotemplates] = useState<Biotemplate[]>([]);
    const [reservedBiotemplates, setReservedBiotemplates] = useState<Biotemplate[]>([]);
    const [jointBiotemplates, setJointBiotemplates] = useState<Biotemplate[]>([]);
    const [additionalBiotemplates, setAdditionalBiotemplates] = useState<Biotemplate[]>([]);
    const [notApplicableBiotemplates, setNotApplicableBiotemplates] = useState<Biotemplate[]>([]);
    const [stepSurplusBiotemplates, setStepSurplusBiotemplates] = useState<Biotemplate[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [isJointActive, setIsJointActive] = useState(false);
    const [isOutJointActive, setIsOutJointActive] = useState(false);
    const [isReqByCustomerActive, setIsReqByCustomerActive] = useState(false);
    const [isNotApplicableStepActive, setIsNotApplicableStepActive] = useState(false);
    const [generalAllocationNotNeeded, setGeneralAllocationNotNeeded] = useState(false);
    const [isAboveMandateAfterFulfillActive, setIsAboveMandateAfterFulfillActive] = useState(false);
    const [isSurplusActive, setIsSurplusActive] = useState(false);
    const [percentageOffroadGeneral, setPercentageOffroadGeneral] = useState(0);
    const [steps, setSteps] = useState<{[key: string]: any}>({
        1: {
            name: "Surplus volumes",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        2: {
            name: "General Mandate",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        3: {
            name: "Reserved Above the Mandate",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        4: {
            name: "Joint Fulfillment OUT",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: true,
            data: [],
            allocated: {},
        },
        5: {
            name: "Additional Volume Above",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        6: {
            name: "Volumes not Applicable to the Mandate",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        7: {
            name: "Matching",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        8: {
            name: "Customer Requirements",
            isDirty: false,
            status: "",
            isComplete: false,
            isDisabled: false,
            data: [],
            allocated: {},
        },
        9: {
            name: "Draft Report",
            isDirty: false,
            isDisabled: false,
            status: "",
        },
    });
    const updateIsFetching = (value: boolean) => {
        setIsFetching(value);
    };

    const isStepDisabled = (stepNumber: number, stepStatusData?: any) => {
        switch (stepNumber) {
            case 1:
                return !isSurplusActive;
            case 3:
                return !isReqByCustomerActive;
            case 4:
                return !isOutJointActive;
            case 5:
                if (stepStatusData) {
                    const step1Status = stepStatusData[1];
                    return !((step1Status === 2 || step1Status === 3) && (stepStatusData[4] != 0));
                }
                return !isAboveMandateAfterFulfillActive;
            case 6:
                return !isNotApplicableStepActive;
            default:
                return false;
        }
    };

    const getStepStatus = async (currentStep?: number) => {
        try {
            const data = await getStepStatusOffroad();
            if (data) {
                // If currentStep is provided, calculate next active step
                let returnStep: number | null = null;
                if (currentStep !== undefined) {
                    let nextStep = currentStep + 1;
                    while (nextStep <= 9) {
                        if (isStepDisabled(nextStep, data.step_status)) {
                            nextStep++;
                        } else {
                            break;
                        }
                    }
                    returnStep = nextStep <= 9 ? nextStep : null;
                }

                setSteps(prevSteps => {
                    const updatedSteps = { ...prevSteps };

                    data.step_status.forEach((statusValue: number, index: number) => {
                        const stepKey = index + 1;

                        if (!returnStep || stepKey < returnStep) {
                            if (statusValue === 1 && stepKey === 2) {
                                setIsAboveMandateAfterFulfillActive(false);
                            } else if ((statusValue === 2 || statusValue === 3) && stepKey === 2 && data.step_status[4] !== 0) {
                                setIsAboveMandateAfterFulfillActive(true);
                            }

                            let status = null;
                            if (statusValue === 1) {
                                status = "Not Complete";
                            } else if (statusValue === 2) {
                                status = "Complete";
                            } else if (statusValue === 3) {
                                status = "Overallocated";
                            }

                            if (updatedSteps[stepKey]) {
                                updatedSteps[stepKey] = {
                                    ...updatedSteps[stepKey],
                                    status,
                                };
                            }
                        } else {
                            updatedSteps[stepKey] = {
                                ...updatedSteps[stepKey],
                                status: null,
                            };
                        }
                    });

                    return updatedSteps;
                });

                return returnStep;
            }
            return data;
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error("Error fetching step status:", error);
            return null;
        }
    };

    const checkData = async () => {
        const offRoadUse = await hasOffRoadData();
        const isSurplusActiveVal = offRoadUse ? offRoadUse.use_roaduse_surplus : false;

        let initialStep = 1;
        if (offRoadUse && parseInt(offRoadUse.last_active_step) > 0) {
            initialStep = parseInt(offRoadUse.last_active_step);
        } else if (!isSurplusActiveVal) {
            initialStep = 2;
        }
        setActiveStep(initialStep);
        setIsJointActive(offRoadUse ? offRoadUse.joint_fulfillment : false);
        setIsReqByCustomerActive(offRoadUse ? offRoadUse.req_by_customer : false);
        setIsOutJointActive(offRoadUse ? offRoadUse.out_joint_fulfillment : false);
        setIsNotApplicableStepActive(offRoadUse ? offRoadUse.not_applicable_step : false);
        setIsAboveMandateAfterFulfillActive(offRoadUse ? offRoadUse.above_mandate_after_fulfill_step_active : false);
        setIsSurplusActive(isSurplusActiveVal);
        setPercentageOffroadGeneral(offRoadUse ? offRoadUse.general_mandate : false);
        if (offRoadUse) {
            getStepStatus();
        }
    };

    const fetchBiotemplateData = async () => {
        const response = await startAllocation("offroad");
        if (response && response.valid) {
            setStepSurplusBiotemplates(
                Array.isArray(response.surplus_biotemplates) ? response.surplus_biotemplates : []);
            setGeneralBiotemplates(response.offroad_biotemplates && response.offroad_biotemplates.general || []);
            setReservedBiotemplates(response.offroad_biotemplates && response.offroad_biotemplates.reserved || []);
            setJointBiotemplates(response.offroad_biotemplates && response.offroad_biotemplates.joint || []);
            setAdditionalBiotemplates(response.offroad_biotemplates && response.offroad_biotemplates.additional || []);
            setNotApplicableBiotemplates(
                response.offroad_biotemplates && response.offroad_biotemplates.notApplicable || []);
            setGeneralAllocationNotNeeded(
                response.offroad_biotemplates && response.offroad_biotemplates.generalAllocationNotNeeded || false );

            const availableData = {};
            // @ts-ignore
            response.biotemplates.forEach((item: Biotemplate) => {
                // @ts-ignore
                availableData[item.biotemplate_id] = item.available_quantity;
            });
            // @ts-ignore
            setAvailableVol(availableData);
        } else {
            alert("Some error occured");
        }
    };

    useEffect(() => {
        setIsFetching(true);
        checkData();
        fetchBiotemplateData();
        setIsFetching(false);
    }, []);

    useEffect(() => {
        setSteps(prevSteps => ({
            ...prevSteps,
            3: {
                ...prevSteps[3],
                isDisabled: !isReqByCustomerActive,
            },
        }));
    }, [isReqByCustomerActive]);
    useEffect(() => {
        setSteps(prevSteps => ({
            ...prevSteps,
            4: {
                ...prevSteps[4],
                isDisabled: !isOutJointActive,
            },
        }));
    }, [isOutJointActive]);
    useEffect(() => {
        setSteps(prevSteps => ({
            ...prevSteps,
            5: {
                ...prevSteps[5],
                isDisabled: !isAboveMandateAfterFulfillActive,
            },
        }));
    }, [isAboveMandateAfterFulfillActive]);
    useEffect(() => {
        setSteps(prevSteps => ({
            ...prevSteps,
            6: {
                ...prevSteps[6],
                isDisabled: !isNotApplicableStepActive,
            },
        }));
    }, [isNotApplicableStepActive]);
    useEffect(() => {
        setSteps(prevSteps => ({
            ...prevSteps,
            1: {
                ...prevSteps[1],
                isDisabled: !isSurplusActive,
            },
        }));
    }, [isSurplusActive]);
    return (
        <>
            <div className={clsx(classes.root)}>
                <div className={classes.textContainer}>
                    <Typography variant="h6">
                        Allocation Mode:
                        <span className={classes.textPrimary}>{isSimulation ? "Simulation" : "Reporting"}</span>
                    </Typography>
                </div>
                <div className={classes.textContainer}>
                    <Typography variant="h6">
                        Mandate:
                        <span className={classes.textPrimary}>Off-road</span>
                    </Typography>
                </div>
                <div className={classes.textContainer}>
                    <Typography variant="h6">
                        Period:
                        <span className={classes.textPrimary}>{periodName ? periodName.year : ""}</span>
                    </Typography>
                </div>
                <div className={classes.textContainer}>
                    <Typography variant="h6">
                        Fulfillment Type:
                        <span className={classes.textPrimary}>{isJointActive ? "Joint" : "Own"}</span>
                    </Typography>
                </div>
            </div>
            <Stepper
                alternativeLabel
                nonLinear
                activeStep={activeStep - 1}
                connector={<StepConnector />}
                className={classes.stepper}
            >
                {Object.keys(steps).map((rank, index) => (
                    <Step key={index}>
                        <StepButton
                            onClick={() => console.log(activeStep)}
                            disabled={steps[rank].isDisabled}
                        >
                            <StepLabel
                                StepIconComponent={props => (
                                    <StepIcon
                                        disabled={steps[rank].isDisabled}
                                        status={steps[rank].isDisabled ? null : steps[rank].status}
                                        {...props}
                                    ></StepIcon>
                                )}
                            >
                                {steps[rank].name}
                            </StepLabel>
                        </StepButton>
                    </Step>
                ))}
            </Stepper>
            { isFetching ? (
                <LoadingView text={"Data is getting prepared"} />
            ) : (
                <>
                    <OffRoadMainCardTables
                        step={activeStep}
                        reqByCustomerDisabled={!isReqByCustomerActive}
                        outJointDisabled={!isOutJointActive}
                        aboveMandateAfterFulfillDisabled={!isAboveMandateAfterFulfillActive}
                        surplusStepDisabled={!isSurplusActive}
                        notApplicableStepDisabled={!isNotApplicableStepActive}
                        handleNext={async () => {
                            setIsFetching(true);
                            if (activeStep && activeStep > 0 && activeStep < 9) {
                                const nextStep = await getStepStatus(activeStep);
                                if (nextStep) {
                                    setActiveStep(nextStep);
                                    await fetchBiotemplateData();
                                }
                            }
                            setIsFetching(false);
                        }}
                        handleBack={async () => {
                            if (activeStep > 1) {
                                setActiveStep(activeStep - 1);
                                if (activeStep < 8) {
                                    fetchBiotemplateData();
                                }
                            }}}
                        updateIsFetching={updateIsFetching}
                        offroadGeneralPercentage={percentageOffroadGeneral}
                        generalBiotemplates={generalBiotemplates}
                        reservedBiotemplates={reservedBiotemplates}
                        jointBiotemplates={jointBiotemplates}
                        additionalBiotemplates={additionalBiotemplates}
                        notApplicableBiotemplates={notApplicableBiotemplates}
                        generalAllocationNotNeeded={generalAllocationNotNeeded}
                        surplusBiotemplates={stepSurplusBiotemplates}
                    />
                </>
            )}
        </>
    );
};

export default OffRoadContainer;
